import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation } from '@tanstack/react-query'
import { toast } from 'sonner'
import { CheckCircle, Mail, MessageCircle, Calendar, Receipt, Send } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useAuth } from '@/auth'
import { solicitarCotizacion } from '../api/pricing.api'
import { formatearPrecio, formatearNumero } from '../lib/pricing-tiers'

const formSchema = z.object({
  nombre: z.string().min(1, 'El nombre es obligatorio'),
  email: z.string().email('Email no válido'),
  empresa: z.string().optional(),
})

type FormValues = z.infer<typeof formSchema>

interface ServicioActivo {
  label: string
  volumen: number
  precioUnitario: number
  subtotal: number
}

interface PricingQuoteModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  serviciosActivos: ServicioActivo[]
  precioTotal: number
  meses: number
  volCorreo: number
  volWhatsapp: number
  volCorreoWhatsapp: number
}

function getServiceIcon(label: string) {
  if (label === 'Correo') return <Mail className="h-[18px] w-[18px] text-teal-700" />
  if (label === 'WhatsApp') return <MessageCircle className="h-[18px] w-[18px] text-teal-500" />
  return (
    <>
      <Mail className="h-4 w-4 text-teal-700" />
      <MessageCircle className="h-4 w-4 text-teal-500" />
    </>
  )
}

export function PricingQuoteModal({
  open,
  onOpenChange,
  serviciosActivos,
  precioTotal,
  meses,
  volCorreo,
  volWhatsapp,
  volCorreoWhatsapp,
}: PricingQuoteModalProps) {
  const { user, isAuthenticated } = useAuth()
  const [enviado, setEnviado] = useState(false)

  const { register, handleSubmit, formState: { errors }, reset } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      nombre: '',
      email: '',
      empresa: '',
    },
  })

  const mutation = useMutation({
    mutationFn: solicitarCotizacion,
    onSuccess: () => {
      setEnviado(true)
      toast.success('Cotización enviada exitosamente')
    },
    onError: () => {
      toast.error('Error al enviar la cotización')
    },
  })

  const onSubmit = (data: FormValues) => {
    mutation.mutate({
      nombre: isAuthenticated ? user?.first_name || user?.username || '' : data.nombre,
      email: isAuthenticated ? user?.email || '' : data.email,
      empresa: isAuthenticated ? undefined : data.empresa || undefined,
      volumen_correo: volCorreo,
      volumen_whatsapp: volWhatsapp,
      volumen_correo_whatsapp: volCorreoWhatsapp,
      frecuencia: 1,
      meses,
    })
  }

  const handleClose = (isOpen: boolean) => {
    if (!isOpen) {
      setEnviado(false)
      reset()
    }
    onOpenChange(isOpen)
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-gray-900 flex items-center">
            <Send className="w-6 h-6 text-teal-500 mr-2" />
            Solicitar Cotización
          </DialogTitle>
        </DialogHeader>

        {enviado ? (
          <div className="text-center py-8 space-y-3">
            <CheckCircle className="h-16 w-16 text-teal-500 mx-auto" />
            <h3 className="text-lg font-bold text-gray-800">Cotización enviada</h3>
            <p className="text-sm text-gray-500">
              Hemos recibido tu solicitud. Nos pondremos en contacto contigo pronto.
            </p>
            <Button onClick={() => handleClose(false)} className="mt-4 bg-teal-800 hover:bg-teal-900 text-white">
              Cerrar
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {/* Summary */}
            <div
              className="rounded-xl p-4 space-y-2"
              style={{
                background: 'linear-gradient(to right, #f0fdfa, transparent)',
                border: '1px solid #0d9488',
              }}
            >
              <h3 className="font-semibold text-gray-900 mb-3 text-sm">Resumen de tu cotización:</h3>

              {serviciosActivos.map((s) => (
                <div
                  key={s.label}
                  className="flex justify-between items-center py-2 border-b border-gray-200"
                >
                  <span className="text-sm text-gray-600 flex items-center gap-1">
                    {getServiceIcon(s.label)} {s.label}:
                  </span>
                  <span className="text-sm font-semibold text-gray-900">
                    {formatearNumero(s.volumen)} contactos
                  </span>
                </div>
              ))}

              <div className="flex justify-between items-center py-2 border-b border-gray-200">
                <span className="text-sm text-gray-600 flex items-center gap-1">
                  <Calendar className="h-4 w-4" /> Duración:
                </span>
                <span className="text-sm font-semibold text-gray-900">
                  {meses} {meses === 1 ? 'mes' : 'meses'}
                </span>
              </div>

              <div className="flex justify-between items-center pt-3 mt-2 border-t-2 border-teal-500">
                <span className="font-bold text-teal-500 text-lg flex items-center gap-1">
                  <Receipt className="h-4 w-4" /> TOTAL:
                </span>
                <span className="font-bold text-teal-500 text-lg">
                  {formatearPrecio(precioTotal)}
                </span>
              </div>
            </div>

            {/* Form Fields */}
            {isAuthenticated ? (
              <div className="space-y-3">
                <div>
                  <Label className="text-xs text-gray-500">Nombre</Label>
                  <Input value={user?.first_name || user?.username || ''} disabled className="bg-gray-50" />
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Email</Label>
                  <Input value={user?.email || ''} disabled className="bg-gray-50" />
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div>
                  <Label htmlFor="nombre" className="text-sm font-semibold text-gray-700">Nombre completo *</Label>
                  <Input id="nombre" {...register('nombre')} placeholder="Ingresa tu nombre completo" />
                  {errors.nombre && <p className="text-xs text-red-500 mt-1">{errors.nombre.message}</p>}
                </div>
                <div>
                  <Label htmlFor="email" className="text-sm font-semibold text-gray-700">Email *</Label>
                  <Input id="email" type="email" {...register('email')} placeholder="tu@email.com" />
                  {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email.message}</p>}
                </div>
                <div>
                  <Label htmlFor="empresa" className="text-sm font-semibold text-gray-700">Empresa / Organización</Label>
                  <Input id="empresa" {...register('empresa')} placeholder="Nombre de tu empresa" />
                </div>
              </div>
            )}

            <Button
              type="submit"
              className="w-full bg-teal-800 hover:bg-teal-900 text-white font-semibold"
              size="lg"
              disabled={mutation.isPending}
            >
              <Send className="w-4 h-4 mr-2" />
              {mutation.isPending ? 'Enviando...' : 'Enviar Solicitud'}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}
